import type {
  CampaignJoinInfo,
  CampaignMemberRole,
  CreateCampaignInput,
  PublicCampaignJoinInfo,
  UpdateCampaignInput,
  UserCampaignRecord,
  UserCampaignSummary,
} from "@west-marches/shared";
import { apiFetch } from "@/lib/apiClient";

async function readJson<T>(response: Response): Promise<T> {
  const payload = (await response.json().catch(() => ({}))) as T & {
    error?: string;
  };

  if (!response.ok) {
    const message =
      typeof payload.error === "string" && payload.error ? payload.error : "Request failed";
    throw new Error(message);
  }

  return payload;
}

export async function listUserCampaigns(): Promise<UserCampaignSummary[]> {
  const response = await apiFetch("/api/campaigns", { method: "GET" });
  const payload = await readJson<{ campaigns: UserCampaignSummary[] }>(response);
  return payload.campaigns;
}

export async function getUserCampaignByShortId(
  shortId: string,
  options: { asPlayer?: boolean } = {},
): Promise<UserCampaignRecord> {
  const params = options.asPlayer ? "?asPlayer=1" : "";
  const response = await apiFetch(
    `/api/campaigns/by-short-id/${encodeURIComponent(shortId)}${params}`,
    { method: "GET" },
  );
  const payload = await readJson<{ campaign: UserCampaignRecord }>(response);
  return payload.campaign;
}

export async function createUserCampaign(
  input: CreateCampaignInput,
): Promise<UserCampaignRecord> {
  const response = await apiFetch("/api/campaigns", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const payload = await readJson<{ campaign: UserCampaignRecord }>(response);
  return payload.campaign;
}

export async function updateUserCampaign(
  campaignId: string,
  input: UpdateCampaignInput,
): Promise<UserCampaignSummary> {
  const response = await apiFetch(`/api/campaigns/${encodeURIComponent(campaignId)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  const payload = await readJson<{ campaign: UserCampaignSummary }>(response);
  return payload.campaign;
}

export async function deleteUserCampaign(campaignId: string): Promise<void> {
  const response = await apiFetch(`/api/campaigns/${encodeURIComponent(campaignId)}`, {
    method: "DELETE",
  });
  await readJson<{ ok: boolean }>(response);
}

// --- Join-code flow ---

export async function getPublicCampaignJoinInfo(shortId: string): Promise<PublicCampaignJoinInfo> {
  const response = await apiFetch(
    `/api/campaigns/by-short-id/${encodeURIComponent(shortId)}/join-info/public`,
    { method: "GET" },
  );
  return readJson<PublicCampaignJoinInfo>(response);
}

export async function getCampaignJoinInfo(shortId: string): Promise<CampaignJoinInfo> {
  const response = await apiFetch(
    `/api/campaigns/by-short-id/${encodeURIComponent(shortId)}/join-info`,
    { method: "GET" },
  );
  return readJson<CampaignJoinInfo>(response);
}

export async function joinCampaignByCode(
  shortId: string,
  joinCode: string,
): Promise<UserCampaignRecord> {
  const response = await apiFetch(
    `/api/campaigns/by-short-id/${encodeURIComponent(shortId)}/join`,
    { method: "POST", body: JSON.stringify({ joinCode }) },
  );
  const payload = await readJson<{ campaign: UserCampaignRecord }>(response);
  return payload.campaign;
}

// --- Join-code management ---

export async function updateCampaignJoinCode(
  campaignId: string,
  input: { regenerate?: boolean; selfJoinEnabled?: boolean },
): Promise<{ joinCode: string | null }> {
  const response = await apiFetch(
    `/api/campaigns/${encodeURIComponent(campaignId)}/join-code`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return readJson<{ ok: boolean; joinCode: string | null }>(response);
}

// --- Member management ---

export async function updateMemberRole(
  campaignId: string,
  userId: string,
  role: CampaignMemberRole,
): Promise<void> {
  const response = await apiFetch(
    `/api/campaigns/${encodeURIComponent(campaignId)}/members/${encodeURIComponent(userId)}`,
    { method: "PATCH", body: JSON.stringify({ role }) },
  );
  await readJson<{ ok: boolean }>(response);
}

export async function removeCampaignMember(
  campaignId: string,
  userId: string,
): Promise<void> {
  const response = await apiFetch(
    `/api/campaigns/${encodeURIComponent(campaignId)}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
  await readJson<{ ok: boolean }>(response);
}
